/**
 * Base floor palette for enemy-mechanic telegraphs and zones (areas.ts asks this first).
 * Returns null for ordinary areas so they keep the default warm orange look.
 *
 * The mechanic overlays (view.ts) draw on top of these, so soak, beam and pool bases are kept
 * thin: the overlay carries the pips, the core and the poison texture.
 */
import type { RenderArea } from '../../../types';
import { HAZARD } from '../enemyTable';

export interface AreaPalette {
  color: [number, number, number];
  core: [number, number, number];
  fill: number;
  edge: number;
}

const rgb = (c: readonly number[]): [number, number, number] => [c[0]!, c[1]!, c[2]!];

export const MECHANIC_PALETTES = {
  deadly: { color: rgb(HAZARD.deadly), core: rgb(HAZARD.deadlyRim), fill: 0.32, edge: 0.78 },
  soak: { color: rgb(HAZARD.soak), core: rgb(HAZARD.soakPip), fill: 0.08, edge: 0.55 },
  beam: { color: rgb(HAZARD.beamEdge), core: rgb(HAZARD.beamCore), fill: 0.12, edge: 0.6 },
  pool: { color: rgb(HAZARD.poison), core: rgb(HAZARD.poisonRim), fill: 0.06, edge: 0.3 },
} as const satisfies Record<string, AreaPalette>;

export function mechanicPalette(a: Pick<RenderArea, 'hostile' | 'intent' | 'motion'>): AreaPalette | null {
  if (!a.hostile) return null;
  if (a.motion === 'aim') return MECHANIC_PALETTES.beam;
  if (a.motion === 'grow') return MECHANIC_PALETTES.pool;
  if (a.intent === 'soak') return MECHANIC_PALETTES.soak;
  if (a.intent === 'deadly') return MECHANIC_PALETTES.deadly;
  return null;
}
